import { createContext, Fragment, MouseEvent, useEffect, useState } from 'react';
import MenuParams from 'shared/ui/menu-params';
import { Card, Chip, IconButton, Menu, MenuItem, Stack, Table, TableCell, TableRow, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { InvoiceInfoDto, IPagingData, PayOrderDto } from '@sr/dto';
import { GridLayout } from 'shared/ui/grid-layout';
import { RowMenu, useMenu } from 'shared/ui/row-menu';
import { formatDate } from 'utils/date-format-helpers';
import { LinkToInvoice } from 'entities/client-invoices-list/ui/link-to-invoice';
import { InvoiceStatusSpan } from '../status-span/status-span';
import PriceSpan from 'shared/ui/price-span';
import { LinkToLegalEntity } from 'shared/ui/links/link-to-legal-entity';
import { LinkToCompany } from 'shared/ui/links/link-to-company';
import ManagerSpan from 'shared/ui/manager-span';
import { IRemotePagedCollection } from 'utils/remote-paged-collection.hook';
import { InvoiceFilter, InvoiceFilterProps } from 'features/invoice/invoice-filter/invoice-filter';
import { WccButton } from 'entities/wcc/wcc-button';
import { LinkToPurchase } from 'shared/ui/links/link-to-purchase';
import { LinkToPayOrder } from 'shared/ui/links/link-to-payorder';
import { TextWithHighlight } from 'shared/ui/text-with-highlight';
import { ListTotalsCard } from 'shared/ui/totals-card';

const columns = ['Дата выставления счета', 'Номер', 'Период закрытия', 'Фирма', 'Юрлицо продавца', 'Наше юрлицо', 'Менеджер', 'Сумма', 'Статус', 'Заявки', 'ПП', 'Акт', ''];

const VISIBLE_PAY_ORDERS_COUNT = 1;

type Props = {
  collection: IRemotePagedCollection<InvoiceInfoDto>;
  paging: IPagingData;
  onChangePaging: (paging: IPagingData) => void;
  onDelete?: (invoice: InvoiceInfoDto) => void;
  onCancel?: (invoice: InvoiceInfoDto) => void;
  invoiceFilterProps?: Omit<InvoiceFilterProps, 'withClosingPeriodFilter'>;
};

export const SupplierInvoicesList = ({ collection, paging, onChangePaging, onDelete, onCancel, invoiceFilterProps }: Props) => {
  const { openMenu, menuData } = useMenu<InvoiceInfoDto>({ deleteHandler: onDelete, cancelHandler: onCancel });
  const metaPriceTotal = collection.collection.meta?.find((x) => x['discountPriceTotal'] !== undefined)?.['discountPriceTotal'];


  return (
    <>
      <InvoiceFilter {...invoiceFilterProps} />
      <GridLayout<InvoiceInfoDto>
        columns={columns}
        items={collection.collection.items}
        isLoading={collection.isLoading}
        error={collection.error}
        pagingData={paging}
        onChangePaging={onChangePaging}
        totalItemsCount={collection.collection.totalItemsCount}
        noItemsText="Список счетов пуст"
        itemRender={(i) => <InvoiceRow key={i.id} invoice={i} onMenuClick={openMenu} />}
        footer={(items) => <InvoiceRowFooter rows={items} metaPriceTotal={metaPriceTotal}/>}
        renderCustomHeader={() => <ListTotalsCard meta={[{ title: 'Сумма', value: metaPriceTotal }]} />}
      />

      <RowMenu menuData={menuData} />
    </>
  );
};

type RowProps = {
  invoice: InvoiceInfoDto;
  onMenuClick: (menuParams: MenuParams<InvoiceInfoDto>) => void;
};

const InvoiceRow = (props: RowProps) => {
  const { invoice, onMenuClick } = props;

  const priceTotal = invoice.totals.priceTotal;
  const discountPriceTotal = invoice.totals.discountPriceTotal;

  const handleMenuClick = (e: MouseEvent<HTMLElement>) => {
    onMenuClick({ anchor: e.currentTarget, target: invoice });
  };

  return (
    <>
      <TableRow key={invoice.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell>
          <LinkToInvoice invoice={invoice}>{formatDate(invoice.startDate)}</LinkToInvoice>
        </TableCell>
        <TableCell>
          <LinkToInvoice invoice={invoice} />
        </TableCell>
        <TableCell>{formatDate(invoice.startDate)}</TableCell>
        <TableCell>
          <LinkToCompany company={invoice.sellerCompany} />
        </TableCell>
        <TableCell>
          <LinkToLegalEntity legalEntity={invoice.seller} />
        </TableCell>
        <TableCell>
          <LinkToLegalEntity legalEntity={invoice.buyer} />
        </TableCell>
        <TableCell>
          {invoice.manager ? <ManagerSpan person={invoice.manager}/> : '-'}
        </TableCell>
        <TableCell>
          <PriceSpan size="small" price={priceTotal} />
        </TableCell>
        <TableCell>
          <InvoiceStatusSpan status={invoice.status} />
        </TableCell>
        <TableCell>
          {invoice.report?.rows.map((x) =>
            x.purchase ? (
              <>
                <br />
                <LinkToPurchase purchase={x.purchase}>№{x.purchase?.id}</LinkToPurchase>
              </>
            ) : (
              <></>
            ),
          )}
        </TableCell>
        <TableCell>
          <PayOrdersCell payOrders={invoice.report?.clientPayments ?? []} />
        </TableCell>
        <TableCell>
          <WccButton invoiceId={invoice.id} invoiceType={invoice.type} initialWcc={invoice.wcc} />
        </TableCell>
        <TableCell align="right">
          <IconButton edge="end" onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    </>
  );
};

const PayOrdersCell = ({ payOrders }: { payOrders: PayOrderDto[] }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!payOrders.length) return <>-</>;

  const visible = payOrders.slice(0, VISIBLE_PAY_ORDERS_COUNT);
  const hidden = payOrders.slice(VISIBLE_PAY_ORDERS_COUNT);

  return (
    <Typography variant="body2" component="span" whiteSpace="nowrap">
      {visible.map((payOrder, idx) => (
        <Fragment key={payOrder.id}>
          {idx > 0 && ', '}
          <LinkToPayOrder payorder={payOrder}>№{payOrder.documentNumber}</LinkToPayOrder>
        </Fragment>
      ))}
      {hidden.length > 0 && (
        <>
          <Chip sx={{ ml: 0.5 }} size="small" label={`+${hidden.length}`} onClick={(e) => setAnchor(e.currentTarget)} />
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            {hidden.map((payOrder) => (
              <MenuItem key={payOrder.id} onClick={() => setAnchor(null)}>
                <LinkToPayOrder payorder={payOrder}>№{payOrder.documentNumber}</LinkToPayOrder>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Typography>
  );
};

type InvoiceRowFooterProps = {
  rows: InvoiceInfoDto[];
  metaPriceTotal?: number,
};
//TODO improve logic with colspan
const InvoiceRowFooter = ({ rows, metaPriceTotal }: InvoiceRowFooterProps) => {
  const priceTotal = metaPriceTotal ?? rows.reduce((acc, row) => acc + (row.totals?.priceTotal || 0), 0);
  return (
    <TableRow>
      <TableCell />
      <TableCell colSpan={6} align="right">
        <Typography variant="subtitle1">Итого:</Typography>
      </TableCell>

      <TableCell align="right">
        <PriceSpan price={priceTotal} size="small" />
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell />
    </TableRow>
  );
};
